// super-excalidraw: Magic-Move presentation morph helpers.
//
// Inspired by github.com/excalidraw-smart-presentation: frames are slides;
// elements that persist across consecutive frames (matched by a shared
// `customData.morphId`) glide/resize between slides instead of hard-cutting.
//
// These are pure helpers — the PresentationPlayer renders a deterministic
// "stage" (each frame letterboxed into the viewport) from per-element SVG
// snapshots and CSS-transitions matched elements from their slide-A rect to
// their slide-B rect. Keeping the math here makes it testable and keeps the
// component lean.

export type AnyEl = any;

export type StageFit = {
  scale: number;
  offsetX: number;
  offsetY: number;
};

export type ElRect = {
  left: number;
  top: number;
  width: number;
  height: number;
  angle: number; // radians
};

// A morph identity shared by an element and its copies in other frames. Set by
// the "duplicate into next frame" command (Ctrl+Shift+D). Elements without one
// never morph — they just crossfade.
export const morphIdOf = (el: AnyEl): string | null =>
  (el && el.customData && typeof el.customData.morphId === "string"
    ? el.customData.morphId
    : null);

// Live, non-frame children of a frame, in z-order (scene array order).
export const frameChildren = (elements: AnyEl[], frameId: string): AnyEl[] =>
  elements.filter(
    (e) => !e.isDeleted && e.type !== "frame" && e.frameId === frameId,
  );

// Find a frame element by id.
export const findFrame = (elements: AnyEl[], id: string): AnyEl | null =>
  elements.find((e) => e.id === id && e.type === "frame" && !e.isDeleted) ??
  null;

// Fit a frame's bounds into a stage of (stageW × stageH) with `pad` px of
// breathing room on each side, centred (letterbox). Returns the scene→stage
// transform.
export const stageFit = (
  frame: AnyEl,
  stageW: number,
  stageH: number,
  pad = 24,
): StageFit => {
  const availW = Math.max(1, stageW - pad * 2);
  const availH = Math.max(1, stageH - pad * 2);
  const scale = Math.min(availW / frame.width, availH / frame.height);
  const drawW = frame.width * scale;
  const drawH = frame.height * scale;
  return {
    scale,
    offsetX: (stageW - drawW) / 2,
    offsetY: (stageH - drawH) / 2,
  };
};

// Map an element's scene rect into stage pixels for a given frame + fit.
export const elRect = (el: AnyEl, frame: AnyEl, fit: StageFit): ElRect => ({
  left: fit.offsetX + (el.x - frame.x) * fit.scale,
  top: fit.offsetY + (el.y - frame.y) * fit.scale,
  width: el.width * fit.scale,
  height: el.height * fit.scale,
  angle: el.angle ?? 0,
});

// A single renderable item on the stage for one slide.
export type StageItem = {
  // Stable key: morphId when present (so the same DOM node persists across
  // slides and CSS-transitions), else the element id (unique per slide).
  key: string;
  morphId: string | null;
  elementId: string;
  rect: ElRect;
  opacity: number;
  enter: string | null; // customData.enter — entrance style for the live player
  loop: string | null;  // customData.loop — continuous motion
};

// Build the stage items for a slide (frame), given the stage size.
export const slideItems = (
  elements: AnyEl[],
  frameId: string,
  stageW: number,
  stageH: number,
  pad?: number,
): StageItem[] => {
  const frame = findFrame(elements, frameId);
  if (!frame) return [];
  const fit = stageFit(frame, stageW, stageH, pad);
  return frameChildren(elements, frameId).map((el) => {
    const mid = morphIdOf(el);
    const cd = el.customData || {};
    return {
      key: mid ? `m:${mid}` : `e:${el.id}`,
      morphId: mid,
      elementId: el.id,
      rect: elRect(el, frame, fit),
      opacity: (el.opacity ?? 100) / 100,
      enter: typeof cd.enter === "string" ? cd.enter : null,
      loop: typeof cd.loop === "string" ? cd.loop : null,
    };
  });
};
